import { configAtom } from "@/config.ts";
import { analyticsEnabled } from "@/featureFlags.ts";
import type { SiteAmount } from "@/modules/currency/currencyTypes.ts";
import { getEnglishText, type Translatable } from "@/modules/l10n/getTransText.ts";
import { loadThemeScripts } from "@/ScriptLoader.tsx";
import { isBuiltRelease } from "@/utils/environment.ts";
import { loadScript } from "@/utils/loadScript.ts";
import { catching } from "@warrenio/utils/catching";
import { filterNulls } from "@warrenio/utils/collections/filterNulls";
import { makeOnce } from "@warrenio/utils/makeOnce";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { useAtomValue } from "jotai/react";
import { atom } from "jotai/vanilla";
import { useEffect } from "react";
import type { KeysOfUnion } from "type-fest";

//#region Types

//
// NB: The e-commerce events are in GTM format, not GA4.
// The extra information is nested under the `ecommerce` key (instead of the top-level).
//
// Reference:
// - https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm
// - https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#send_ecommerce
//

export interface EcommerceItem {
    item_id: string;
    item_name: string;
    price?: number;
    quantity?: number;
    discount?: number;
    coupon?: string;
    item_brand?: string;
    item_category?: string;
}

export interface BeginCheckoutEvent {
    event: "begin_checkout";
    ecommerce: {
        currency: string;
        value: number; // The total value of the items in the cart
        items: EcommerceItem[];
        coupon?: string;
    };
}

// XXX: This event does not make sense for use since it's supposed to be used as part of a checkout flow
export interface AddPaymentInfoEvent {
    event: "add_payment_info";
    ecommerce: {
        currency: string;
        value: number;
        payment_type: string; // e.g., 'Credit Card', 'PayPal'
        items: EcommerceItem[];
        coupon?: string;
    };
}

export interface PurchaseEvent {
    event: "purchase";
    ecommerce: {
        /**
         * A unique identifier for the transaction. Required for transaction de-duplication.
         */
        transaction_id: string;
        currency: string;
        /**
         * The total value of the transaction, including tax and shipping if applicable.
         */
        value: number;
        items: EcommerceItem[];
        coupon?: string;
        tax?: number;
        shipping?: number;
        customer_type?: "new" | "returning";
    };
}

/** Our internal representation of a top-up (for convenience) */
export interface PaymentInfo {
    transaction_id: string;
    currency: string;
    amount: SiteAmount;
    invoice?: { id: number; title: Translatable };
    method: { id: string; name: string };
}

/** Create an {@link AnalyticsEvent} from our payment parameters */
export function paymentEvent(
    event: "begin" | "success",
    { amount, invoice, currency, method, transaction_id }: PaymentInfo,
): BeginCheckoutEvent | PurchaseEvent {
    const item: EcommerceItem = {
        item_id: invoice ? `pay-invoice-${invoice.id}-${method.id}` : `topup-${method.id}`,
        item_name: invoice
            ? `Payment for ${getEnglishText(invoice.title)} using ${method.name}`
            : `Topup using ${method.name}`,
        item_category: invoice ? "Invoice payment" : "Topup",
        price: amount,
    };

    switch (event) {
        case "begin":
            return {
                event: "begin_checkout",
                ecommerce: { currency, value: amount, items: [item] },
            };
        case "success":
            return {
                event: "purchase",
                ecommerce: { transaction_id, currency, value: amount, items: [item] },
            };
    }
}

export interface PageViewEvent {
    event: "page_view";
    page_location: string;
    page_referrer?: string;
    engagement_time_msec?: number; // NB: Only used by GTM
}

export interface LegacyCustomEvent {
    event: "event";
    event_action: string;
    event_category?: string;
    event_label?: string;
    value?: number;
}

export interface GenerateLeadEvent {
    event: "generate_lead";
    lead_type?: "sign_up";
}

export interface SignupEvent {
    event: "sign_up";
    method?: string;
}

export interface LoginEvent {
    event: "login";
    method?: string;
}

export type AnalyticsEvent =
    | PageViewEvent
    | LegacyCustomEvent
    | GenerateLeadEvent
    | SignupEvent
    | LoginEvent
    | BeginCheckoutEvent
    | AddPaymentInfoEvent
    | PurchaseEvent;

interface AnalyticsProvider {
    load(): void;
    /** NB: This must work even when the provider has not been loaded yet */
    event(event: AnalyticsEvent): void;
}

//#endregion

//#region gtag.js

type GtagDataLayer = IArguments[];

declare global {
    var dataLayer: GtagDataLayer;
}

/** XXX: Need to extend the `gtag.js` type definitions since they are incomplete */
interface GtagConfigParamsFix extends Gtag.ConfigParams {
    debug_mode?: boolean;
}

/** Convert from GTM event format to gtag event format */
function convertToGtagEvent(event: AnalyticsEvent) {
    if ("ecommerce" in event) {
        return {
            ...event,
            ...event.ecommerce,
        };
    }
    return event;
}

const initGtag = makeOnce((tagId: string, config: GtagConfigParamsFix): AnalyticsProvider => {
    window.dataLayer ||= [];

    window.gtag = function gtag() {
        /* eslint-disable prefer-rest-params */
        // biome-ignore lint/complexity/noArguments: The SDK requires using the `arguments` object
        window.dataLayer.push(arguments);
        /* eslint-enable prefer-rest-params */
    };

    window.gtag("js", new Date());
    window.gtag("config", tagId, config);

    return {
        load() {
            const scriptUrl = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
            // Load in the background since we stub out the gtag function
            discardPromise(loadScript(scriptUrl));

            gtagEvent("initialize");
        },
        event(event) {
            const { event: eventType, ...params } = convertToGtagEvent(event);
            gtagEvent(eventType, params);
        },
    };
});

function gtagEvent(event: string, params?: Record<string, any>) {
    window.gtag("event", event, params);
}

//#endregion

//#region Google Tag Manager

type GtmDataLayer = Record<string, any>[];

function getGtmDataLayer(dataLayerName: string) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return (((window as any)[dataLayerName] as GtmDataLayer) ||= []);
}

interface GtmRawEvent {
    event: string;
    [key: string]: unknown;
}

/** Translate to legacy GTM format (used by old UI) */
function convertToLegacyGtmEvent(event: AnalyticsEvent): GtmRawEvent | null {
    const type = event.event;
    switch (type) {
        case "page_view":
            return {
                event: "pageview",
                page: {
                    url: event.page_location,
                },
            };
        case "event":
            return {
                event: "event",
                eventProps: {
                    action: event.event_action,
                    category: event.event_category,
                    label: event.event_label,
                    value: event.value,
                },
            };
        default:
            // For other event types, return them as-is. This assumes that the actual GTM configuration is forward-compatible.
            return { ...event };
    }
}

/** Fields that can persist between events */
const persistentFields: KeysOfUnion<AnalyticsEvent>[] = ["page_location", "page_referrer"];

/** Produce an event that _clears_ any fields set by the original {@link event} */
function makeClearingEvent(event: object) {
    let obj: Record<string, unknown> | undefined;
    for (const key in event) {
        if (key === "event" || persistentFields.includes(key)) continue;

        (obj ??= {})[key] = undefined;
    }
    return obj;
}

// NB: `dataLayerName` should not be "dataLayer" to prevent conflicts with `gtag.js`
const initGtm = makeOnce((containerId: string, dataLayerName: string = "gtmDataLayer"): AnalyticsProvider => {
    {
        const dl = getGtmDataLayer(dataLayerName);
        dl.push({ "gtm.start": Date.now(), event: "gtm.js" });
    }

    return {
        load() {
            const scriptUrl = `https://www.googletagmanager.com/gtm.js?id=${containerId}&l=${dataLayerName}`;
            discardPromise(loadScript(scriptUrl));
        },
        event(event) {
            const finalEvent = legacyConversionEnabled ? convertToLegacyGtmEvent(event) : event;
            if (!finalEvent) {
                return;
            }

            const dl = getGtmDataLayer(dataLayerName);
            dl.push(finalEvent);

            // In GTM, field values "stick around" in the data layer for any subsequent, possibly unrelated events,
            // so we need to clear them to prevent confusion.
            const clear = makeClearingEvent(finalEvent);
            if (clear) {
                dl.push(clear);
            }
        },
    };
});

//#endregion

//#region Provider initialization

const gtagAtom = atom((get) => {
    const { google_analytics_key } = get(configAtom);
    if (!google_analytics_key) return null;

    return initGtag(google_analytics_key, { debug_mode: !isBuiltRelease, send_page_view: false });
});

const gtmAtom = atom((get) => {
    const { google_tag_manager_key } = get(configAtom);
    if (!google_tag_manager_key) return null;

    return initGtm(google_tag_manager_key);
});

const providersAtom = atom<AnalyticsProvider[]>((get) => filterNulls([get(gtagAtom), get(gtmAtom)]));

//#endregion

////// Configuration
const legacyConversionEnabled = false;
const verboseDebug = false;

//#region Public interface

/** @public */
export class AnalyticsManager {
    constructor(private providers: AnalyticsProvider[]) {}

    event(event: AnalyticsEvent) {
        if (event.event !== "page_view" || verboseDebug) {
            logEvent(event);
        }

        for (const provider of this.providers) {
            catching(() => provider.event(event));
        }
    }
}

function logEvent(event: AnalyticsEvent) {
    const { event: eventType, ...params } = event;
    console.debug("[analytics] %s: %o", eventType, params);
}

const analyticsAtom = atom((get) => new AnalyticsManager(get(providersAtom)));

export function useAnalytics(): AnalyticsManager {
    return useAtomValue(analyticsAtom);
}

/** Load third-party analytics scripts according to configuration */
export function useInitializeAnalytics() {
    const providers = useAtomValue(providersAtom);

    useEffect(() => {
        if (!analyticsEnabled) {
            return;
        }

        // NB: Load analytics after any user scripts (in case they are used for consent)
        discardPromise(
            loadThemeScripts().then(() => {
                console.debug("Initializing analytics providers...");
                for (const provider of providers) {
                    provider.load();
                }
            }),
        );
    }, [providers]);
}

//#endregion
